import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AlbertoGallery from "./AlbertoGallery";

// normal gallery photos
import mem1 from "../accests/g/g1 (12).jpeg";
import mem2 from "../accests/g/g1 (10).jpeg";
import mem3 from "../accests/g/g1 (16).jpeg";

import alb1 from "../accests/alberto/a1.jpeg";
import alb2 from "../accests/alberto/a2.jpeg";
import alb3 from "../accests/alberto/a3.jpeg";
import alb4 from "../accests/alberto/a4.jpeg";
import alb5 from "../accests/alberto/a5.jpeg";
import alb6 from "../accests/alberto/a6.jpeg";
import alb7 from "../accests/alberto/a7.jpeg";
import alb8 from "../accests/alberto/a8.jpeg";
import alb9 from "../accests/alberto/a9.jpeg";
import alb10 from "../accests/alberto/a10.jpeg";
import alb11 from "../accests/alberto/a11.jpeg";
import alb12 from "../accests/alberto/a12.jpeg";
import alb13 from "../accests/alberto/a13.jpeg";
import alb14 from "../accests/alberto/a14.jpeg";
import alb15 from "../accests/alberto/a15.jpeg";
import alb16 from "../accests/alberto/a16.jpeg";
import alb17 from "../accests/alberto/a17.jpeg";
import alb18 from "../accests/alberto/a18.jpeg";
import alb19 from "../accests/alberto/a19.jpeg";
import alb20 from "../accests/alberto/a20.jpeg";
import alb21 from "../accests/alberto/a21.jpeg";
import alb22 from "../accests/alberto/a22.mp4";
import alb23 from "../accests/alberto/a23.mp4";
import alb24 from "../accests/alberto/a24.mp4";

/* ---------- tiny inline icons ---------- */
const IconChevronLeft = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
        <polyline points="15 18 9 12 15 6" />
    </svg>
);
const IconChevronRight = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
        <polyline points="9 18 15 12 9 6" />
    </svg>
);
const IconClose = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

export default function GalleryPreview() {

    const [showAlberto, setShowAlberto] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);

    const previewImages = [mem1, mem2, mem3];

    const albertoImages = [
        alb1, alb2, alb3, alb4, alb5, alb6, alb7, alb8, alb9, alb10,
        alb11, alb12, alb13, alb14, alb15, alb16, alb17, alb18, alb19,
        alb20, alb21, alb22, alb23, alb24,
    ];

    const openLightbox = (index) => setActiveIndex(index);
    const closeLightbox = () => setActiveIndex(null);

    const showPrev = useCallback(() => {
        setActiveIndex((i) => (i === null ? null : (i - 1 + previewImages.length) % previewImages.length));
    }, [previewImages.length]);

    const showNext = useCallback(() => {
        setActiveIndex((i) => (i === null ? null : (i + 1) % previewImages.length));
    }, [previewImages.length]);

    useEffect(() => {
        if (activeIndex === null) return;
        const handleKey = (e) => {
            if (e.key === "Escape") closeLightbox();
            if (e.key === "ArrowLeft") showPrev();
            if (e.key === "ArrowRight") showNext();
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [activeIndex, showPrev, showNext]);

    if (showAlberto) {
        return <AlbertoGallery images={albertoImages} onBack={() => setShowAlberto(false)} />;
    }

    return (
        <div className="bg-[#EFEDE3] text-[#12302E]">

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;1,9..144,500&family=Inter:wght@400;500&family=IBM+Plex+Mono:wght@400;500&display=swap');
                .font-display { font-family: 'Fraunces', serif; }
                .font-body { font-family: 'Inter', sans-serif; }
                .font-mono { font-family: 'IBM Plex Mono', monospace; }
            `}</style>

            <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">

                <div className="flex items-end justify-between mb-10">
                    <div>
                        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#C98A3E]">
                            A collection, kept
                        </span>
                        <h2 className="font-display italic text-4xl md:text-5xl mt-2">
                            Our Memories
                        </h2>
                    </div>

                    <a
                        href="/gallery"
                        className="hidden sm:inline-flex items-center gap-1 font-body text-sm text-[#12302E]/70 hover:text-[#12302E] transition"
                    >
                        View full gallery <IconChevronRight className="w-4 h-4" />
                    </a>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[170px] md:auto-rows-[220px] gap-3 md:gap-4">

                    {previewImages.map((img, i) => (
                        <motion.button
                            key={i}
                            type="button"
                            onClick={() => openLightbox(i)}
                            whileHover={{ scale: 0.98 }}
                            className={`group relative rounded-2xl overflow-hidden ${
                                i === 0 ? "col-span-2 row-span-2" : ""
                            }`}
                        >
                            <img
                                src={img}
                                alt=""
                                className="w-full h-full object-cover transition duration-500 group-hover:scale-110 group-hover:brightness-90"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition duration-300" />
                        </motion.button>
                    ))}

                    {/* Alberto Album */}
                    <motion.button
                        type="button"
                        onClick={() => setShowAlberto(true)}
                        whileHover={{ scale: 0.98 }}
                        className="relative col-span-2 row-span-2 rounded-2xl overflow-hidden group text-left"
                    >
                        <img
                            src={alb1}
                            alt="Alberto Family Tour"
                            className="w-full h-full object-cover transition duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0D2422]/90 via-[#0D2422]/30 to-transparent" />
                        <span className="absolute -top-2 left-8 w-16 h-6 bg-[#E7B369]/80 rotate-[-6deg] shadow-sm" />

                        <div className="absolute inset-0 flex flex-col items-start justify-end p-6">
                            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#E7B369] mb-2">
                                Featured chapter
                            </span>
                            <h3 className="font-display italic text-2xl md:text-3xl text-white leading-tight">
                                Alberto Family Tour
                                <br />in Sri Lanka
                            </h3>
                        </div>
                    </motion.button>

                </div>

                <a
                    href="/gallery"
                    className="sm:hidden mt-8 inline-flex items-center gap-1 font-body text-sm text-[#12302E]/70"
                >
                    View full gallery <IconChevronRight className="w-4 h-4" />
                </a>

            </div>

            {/* ---------- LIGHTBOX (preview images only) ---------- */}
            <AnimatePresence>
                {activeIndex !== null && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-[#0D2422]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
                        onClick={closeLightbox}
                    >
                        <button
                            onClick={closeLightbox}
                            aria-label="Close"
                            className="absolute top-5 right-5 md:top-8 md:right-8 text-white/80 hover:text-white transition p-2 rounded-full hover:bg-white/10"
                        >
                            <IconClose className="w-6 h-6" />
                        </button>

                        <button
                            onClick={(e) => { e.stopPropagation(); showPrev(); }}
                            aria-label="Previous image"
                            className="absolute left-3 md:left-8 text-white/80 hover:text-white transition p-2 md:p-3 rounded-full hover:bg-white/10"
                        >
                            <IconChevronLeft className="w-7 h-7 md:w-8 md:h-8" />
                        </button>

                        <motion.div
                            key={activeIndex}
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.98 }}
                            transition={{ duration: 0.25 }}
                            className="relative max-w-4xl w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={previewImages[activeIndex]}
                                alt=""
                                className="w-full max-h-[78vh] object-contain rounded-xl shadow-2xl"
                            />
                            <p className="text-center font-mono text-xs tracking-[0.3em] text-white/50 mt-4">
                                {String(activeIndex + 1).padStart(2, "0")} / {String(previewImages.length).padStart(2, "0")}
                            </p>
                        </motion.div>

                        <button
                            onClick={(e) => { e.stopPropagation(); showNext(); }}
                            aria-label="Next image"
                            className="absolute right-3 md:right-8 text-white/80 hover:text-white transition p-2 md:p-3 rounded-full hover:bg-white/10"
                        >
                            <IconChevronRight className="w-7 h-7 md:w-8 md:h-8" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

        </div>
    );
}
